<script setup lang="ts">
import { Coupon } from '@/@types/cart'
import { PaginationData } from '@/@types/order'
import CouponPagination from '@/components/shared/coupons/CouponPagination.vue'
import Filter from '@/components/shared/coupons/Filter.vue'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Switch } from '@/components/ui/switch'
import { TableBody, TableCell } from '@/components/ui/table-custom'
import Table from '@/components/ui/table-custom/Table.vue'
import TableHead from '@/components/ui/table-custom/TableHead.vue'
import TableHeader from '@/components/ui/table-custom/TableHeader.vue'
import TableRow from '@/components/ui/table-custom/TableRow.vue'
import { Title } from '@/components/ui/title'
import { formatPrice } from '@/helpers/current-price'
import { formatDateFull } from '@/helpers/format-date'
import LightLayout from '@/layouts/LightLayout.vue'
import { Head, Link, router } from '@inertiajs/vue3'
import { Edit, Printer } from 'lucide-vue-next'
import { ref } from 'vue'

const props = defineProps<{
    coupons: Coupon[]
    statuses: string[]
    categories: string[]
    workshops: Array<{ id: number; label: string }>
    pagination: PaginationData
    query?: string
    status?: string
    category?: string
    workshop?: string
}>()

const params = ref({
    query: props.query || '',
    status: props.status || '',
    category: props.category || '',
    workshop: props.workshop || '',
})
</script>

<template>
    <Head :title="$t('Coupons')" />

    <LightLayout contentClasses="bg-slate-50">
        <main class="container flex flex-col gap-6">
            <div class="flex flex-wrap items-baseline justify-between gap-6">
                <Title>{{ $t('Manage coupons') }}</Title>
                <div class="flex flex-wrap">
                    <form action="/coupons/export_csv" method="get" class="pr-4">
                        <input type="hidden" name="workshop_id" :value="workshop" />
                        <Button variant="outline" type="submit"><Printer />{{ $t('Export') }}</Button>
                    </form>
                <Button :as="Link" href="/coupons/new">{{ $t('Create new coupon') }}</Button>
                </div>
            </div>

            <Card class="border-0 pt-6">
                <CardContent>
                    <Filter
                        :statuses="statuses"
                        :categories="categories"
                        :workshops="workshops"
                        :status="status"
                        :query="query"
                        :category="category"
                        :workshop="workshop"
                    />

                    <Table class="lg:table-fixed">
                        <TableHeader>
                            <TableRow>
                                <TableHead class="lg:w-64">{{ $t('Code') }}</TableHead>
                                <TableHead>{{ $t('Type') }}</TableHead>
                                <TableHead>{{ $t('Amount') }}</TableHead>
                                <TableHead>{{ $t('Validity') }}</TableHead>
                                <TableHead>{{ $t('Quantity') }}</TableHead>
                                <TableHead>{{ $t('Status') }}</TableHead>
                                <TableHead class="lg:w-30">{{ $t('Actions') }}</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            <TableRow class="cursor-auto" v-for="coupon in coupons" :key="coupon.id">
                                <TableCell class="max-w-64 whitespace-normal break-all">
                                    {{ coupon.code }}
                                </TableCell>
                                <TableCell>
                                    {{ $t(coupon.category) }}
                                </TableCell>
                                <TableCell>
                                    {{ coupon.value_cents && formatPrice(coupon.value_cents / 100) }}
                                    {{ coupon.percentage_value && coupon.percentage_value + '%' }}
                                </TableCell>
                                <TableCell> {{ coupon.end_date && formatDateFull(coupon.end_date, $i18n.locale) }} </TableCell>
                                <TableCell>
                                    <strong class="text-slate-950">{{ coupon.usage_count }}</strong
                                    >/{{ coupon.usage_limit }}
                                </TableCell>
                                <TableCell>
                                    <Badge :variant="coupon.status">
                                        {{ $t(coupon.status) }}
                                    </Badge>
                                </TableCell>
                                <TableCell>
                                    <div class="flex items-center gap-7">
                                        <Link :href="`/coupons/${coupon.id}`" class="aria-disabled:pointer-events-none aria-disabled:opacity-30">
                                            <Edit class="size-5 min-w-5 text-gray-700" />
                                            <span class="sr-only">{{ $t('Edit coupon') }}</span>
                                        </Link>

                                        <Switch
                                            :disabled="coupon.status === 'expired' || coupon.usage_count === coupon.usage_limit"
                                            :defaultValue="coupon.status === 'active'"
                                            @update:modelValue="
                                                (value) => {
                                                    return router.patch(
                                                        `/coupons/${coupon.id}/toggle_active`,
                                                        {
                                                            status: value ? 'active' : 'disabled',
                                                        },
                                                        {
                                                            only: ['coupons', 'flash', 'query', 'status', 'category', 'workshop'],
                                                            preserveUrl: true,

                                                            preserveScroll: true,
                                                        },
                                                    )
                                                }
                                            "
                                        />
                                    </div>
                                </TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>

            <CouponPagination :pagination="pagination" v-if="pagination.last_page > 1" :params="params" />
        </main>
    </LightLayout>
</template>
